import {
  type Carer,
  type Device,
  DeviceStatus,
  DeviceTrailGpsStatus,
  DeviceTrailType,
  type DeviceTrailTypeSignIn
} from '@lmc/ts-common'
import { Tag } from '@lmc/ui-kit'
import { Button, Text } from '@lmc/ui-kit/src/general'
import { dacConstants, dacTranslations } from 'locale/deviceAccessControl'
import type { TFunction } from 'react-i18next'
import styled from 'styled-components'
import { DEVICES_PER_PAGE, type TableParams } from './types'

const StyledText = styled(Text)`
  color: ${(props) => props.theme.color.neutral.color5};
`

export const tableParamsDefaultState = (total?: number) =>
  ({
    pagination: {
      current: 1,
      pageSize: DEVICES_PER_PAGE,
      position: ['bottomCenter'],
      ...(total && { total })
    }
  }) as TableParams

export const getBadgeType = (
  status: DeviceStatus | undefined,
  t: TFunction<'translation', undefined>
) => {
  switch (status) {
    case DeviceStatus.approved:
      return (
        <Tag color="green">{t(dacTranslations.approved)?.toUpperCase()}</Tag>
      )
    case DeviceStatus.blocked:
      return <Tag color="red">{t(dacTranslations.blocked)?.toUpperCase()}</Tag>
    case DeviceStatus.pending:
      return (
        <Tag color="orange">{t(dacTranslations.pending)?.toUpperCase()}</Tag>
      )
    default:
      return <Tag>{t(dacConstants.NO_REQUEST)}</Tag>
  }
}

export const getLocationItemType = (
  type: DeviceTrailType,
  t: TFunction<'translation', undefined>,
  signIn: DeviceTrailTypeSignIn
) => {
  if (
    type === DeviceTrailType.approvalRequest ||
    signIn?.gps?.status === DeviceTrailGpsStatus.notGranted
  ) {
    return <StyledText level={1}>{t(dacConstants.NEEDS_PERMISSION)}</StyledText>
  }

  const lat = signIn?.gps?.coordinates?.lat
  const lng = signIn?.gps?.coordinates?.lng

  const isLocationAvailable = !!lat && !!lng

  if (
    type === DeviceTrailType.signIn &&
    signIn?.gps?.status === DeviceTrailGpsStatus.ok &&
    isLocationAvailable
  ) {
    return (
      <Button
        variant="text"
        // eslint-disable-next-line no-console
        onClick={() =>
          window.open(
            `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`,
            '_blank' // <- This is what makes it open in a new window.
          )
        }
        data-cy="btn-open-location"
      >
        {t(dacConstants.OPEN_IN_MAPS)}
      </Button>
    )
  }
  if (
    type === DeviceTrailType.signIn &&
    (signIn?.gps?.status === DeviceTrailGpsStatus.disabled ||
      !isLocationAvailable)
  ) {
    return <StyledText level={1}>{t(dacConstants.GPS_TURNED_OFF)}</StyledText>
  }
  return null
}

export const getDeviceOptions = (allDevices?: Device[]) =>
  allDevices
    ?.filter(
      (obj, index) =>
        allDevices.findIndex((item) => item.name === obj.name) === index
    )
    ?.map((device) => ({
      value: device.id,
      label: device.name
    }))

export const getCarerOptions = (allCarers?: Carer[]) =>
  allCarers?.map((carer) => ({
    value: carer.id,
    label: `${carer.name.first} ${carer.name.last}`
  }))
